import { useCallback, useRef, useState } from 'react';
import { getMediaRecorderOptions } from '@/config/audio.config';
import {
  pauseRecordingClock,
  recordedMs,
  resumeRecordingClock,
  startRecordingClock,
  type RecordingClock,
} from '@/lib/recording-clock';

/**
 * MediaRecorder of the "Enregistrer" page, moved out of the page unchanged:
 * same recorder options, same blob type fallback, same recorded-time clock
 * (a paused stretch is not part of the audio file).
 */
export function useSpeakRecorder({ onStartError }: { onStartError: (error: unknown) => void }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingDurationMs, setRecordingDurationMs] = useState<number | null>(null);
  // The live microphone stream, for the waveform while recording.
  const [stream, setStream] = useState<MediaStream | null>(null);
  // Counts recorded time only: a paused stretch is not part of the audio file.
  const recordingClockRef = useRef<RecordingClock | null>(null);
  const onStartErrorRef = useRef(onStartError);
  onStartErrorRef.current = onStartError;

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, getMediaRecorderOptions());
      const chunks: Blob[] = [];

      recordingClockRef.current = startRecordingClock(Date.now());
      setIsPaused(false);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/wav' });
        setRecordedBlob(blob);
        setRecordedAudio(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      };
      recorder.start();
      setStream(stream);
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error: unknown) {
      onStartErrorRef.current(error);
    }
  }, []);

  const stopRecording = useCallback(() => {
    mediaRecorder?.stop();
    setIsRecording(false);
    setIsPaused(false);
    setMediaRecorder(null);

    const clock = recordingClockRef.current;
    if (clock) {
      setRecordingDurationMs(recordedMs(clock, Date.now()));
      recordingClockRef.current = null;
    } else {
      console.warn('Recording start time was not captured!');
    }
  }, [mediaRecorder]);

  /** Pauses or resumes the open recording, freezing the recorded duration. */
  const togglePause = useCallback(() => {
    const recorder = mediaRecorder;
    const clock = recordingClockRef.current;
    if (!recorder || !clock) return;
    const now = Date.now();
    if (recorder.state === 'recording') {
      recordingClockRef.current = pauseRecordingClock(clock, now);
      recorder.pause();
      setIsPaused(true);
      return;
    }
    if (recorder.state === 'paused') {
      recordingClockRef.current = resumeRecordingClock(clock, now);
      recorder.resume();
      setIsPaused(false);
    }
  }, [mediaRecorder]);

  /** Forgets the take (after a send, or when moving to another phrase). */
  const resetRecording = useCallback(() => {
    setRecordedAudio(null);
    setRecordedBlob(null);
    setRecordingDurationMs(null);
  }, []);

  return {
    isRecording,
    isPaused,
    recordedAudio,
    recordedBlob,
    recordingDurationMs,
    stream,
    startRecording,
    stopRecording,
    togglePause,
    resetRecording,
  };
}

export type SpeakRecorder = ReturnType<typeof useSpeakRecorder>;
